<script setup>
import { computed, nextTick, reactive, watch } from 'vue'
import { useAppStore } from '../composables/useAppStore'
import { calculateShift } from '../lib/calculator'
import { formatCurrency, formatDate, timeToMinutes, todayISO } from '../lib/date'
import BaseButton from './common/BaseButton.vue'
import BaseInput from './common/BaseInput.vue'
import BaseTextarea from './common/BaseTextarea.vue'
import TimeInput from './common/TimeInput.vue'

const props = defineProps({
  modelValue: { type: Object, default: null },
  conditions: { type: Array, default: () => [] },
  submitLabel: { type: String, default: 'Сохранить смену' },
  loading: { type: Boolean, default: false }
})

const emit = defineEmits(['submit', 'cancel', 'dirty-change'])
const { state } = useAppStore()
const submitted = reactive({ value: false })
const errors = reactive({ startTime: '', endTime: '', orders: '' })
let initialSnapshot = ''

function getDuration(start, end) {
  const startMinutes = timeToMinutes(start)
  let endMinutes = timeToMinutes(end)
  if (startMinutes === null || endMinutes === null || start === end) return 0
  if (endMinutes <= startMinutes) endMinutes += 1440
  return Math.round(((endMinutes - startMinutes) / 60) * 100) / 100
}

function readRememberedValues() {
  if (!state.settings.interface?.rememberShiftValues) return null
  try {
    return JSON.parse(window.localStorage.getItem('dohodpro.lastShiftValues') || 'null')
  } catch {
    return null
  }
}

function createForm(value) {
  const remembered = !value ? readRememberedValues() : null
  const startTime = value?.startTime || remembered?.startTime || '08:00'
  const endTime = value?.endTime || remembered?.endTime || '16:00'
  return {
    id: value?.id || '',
    date: value?.date || todayISO(),
    startTime,
    endTime,
    hours: Number(value?.hours ?? getDuration(startTime, endTime)),
    orders: Number(value?.orders ?? remembered?.orders ?? 0),
    conditionCodes: [...(value?.conditionCodes || [])],
    manualAdjustment: Number(value?.manualAdjustment || 0),
    comment: value?.comment || ''
  }
}

const form = reactive(createForm(props.modelValue))

function snapshot() {
  return JSON.stringify({ ...form, conditionCodes: [...form.conditionCodes].sort() })
}

async function resetFromValue(value) {
  Object.assign(form, createForm(value))
  submitted.value = false
  await nextTick()
  initialSnapshot = snapshot()
  emit('dirty-change', false)
}

watch(() => props.modelValue, resetFromValue, { immediate: true })
watch(form, () => {
  form.hours = getDuration(form.startTime, form.endTime)
  if (initialSnapshot) emit('dirty-change', snapshot() !== initialSnapshot)
}, { deep: true })

const durationLabel = computed(() => form.hours ? `${form.hours.toLocaleString('ru-RU')} ч` : 'Не рассчитана')
const crossesMidnight = computed(() => {
  const start = timeToMinutes(form.startTime)
  const end = timeToMinutes(form.endTime)
  return start !== null && end !== null && end <= start && form.startTime !== form.endTime
})
const preview = computed(() => calculateShift({
  ...form,
  hours: Number(form.hours),
  orders: Number(form.orders || 0),
  manualAdjustment: Number(form.manualAdjustment || 0)
}, state))
const quickDurations = [4, 6, 8, 10, 12]
const quickStarts = ['08:00', '10:00', '12:00', '18:00']

function formatTimeFromMinutes(value) {
  const total = ((Number(value) % 1440) + 1440) % 1440
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

function setDuration(hours) {
  const start = timeToMinutes(form.startTime)
  if (start === null) return
  form.endTime = formatTimeFromMinutes(start + hours * 60)
}

function setStartTime(time) {
  const existingDuration = form.hours || 8
  form.startTime = time
  const start = timeToMinutes(time)
  if (start !== null) form.endTime = formatTimeFromMinutes(start + existingDuration * 60)
}

function useLastShift() {
  const previous = state.shifts[0]
  if (!previous) return
  const currentDate = form.date
  Object.assign(form, { ...createForm(previous), id: '', date: currentDate })
}

function useQuickTemplate() {
  const quick = state.settings.quickShift
  if (!quick) return
  form.startTime = quick.startTime || '08:00'
  form.orders = Number(quick.orders || 0)
  setDuration(Math.max(1, Number(quick.hours || 8)))
}

function clearForm() {
  Object.assign(form, {
    id: '',
    date: form.date || todayISO(),
    startTime: '08:00',
    endTime: '16:00',
    hours: 8,
    orders: 0,
    conditionCodes: [],
    manualAdjustment: 0,
    comment: ''
  })
}

function toggleCondition(code) {
  const index = form.conditionCodes.indexOf(code)
  if (index >= 0) form.conditionCodes.splice(index, 1)
  else form.conditionCodes.push(code)
}

function validate() {
  errors.startTime = form.startTime ? '' : 'Укажите время начала.'
  errors.endTime = !form.endTime
    ? 'Укажите время окончания.'
    : form.startTime === form.endTime
      ? 'Начало и окончание не могут совпадать.'
      : ''
  errors.orders = Number(form.orders) < 0 ? 'Количество заказов не может быть отрицательным.' : ''
  return !errors.startTime && !errors.endTime && !errors.orders && form.hours > 0
}

function submit() {
  if (props.loading) return
  submitted.value = true
  if (!validate()) return

  const payload = {
    ...form,
    hours: Number(form.hours),
    orders: Math.max(0, Number(form.orders || 0)),
    manualAdjustment: Number(form.manualAdjustment || 0),
    conditionCodes: [...form.conditionCodes]
  }

  if (state.settings.interface?.rememberShiftValues) {
    window.localStorage.setItem('dohodpro.lastShiftValues', JSON.stringify({
      startTime: form.startTime,
      endTime: form.endTime,
      orders: payload.orders
    }))
  }

  initialSnapshot = snapshot()
  emit('dirty-change', false)
  emit('submit', payload)
}
</script>

<template>
  <form class="space-y-5" @submit.prevent="submit">
    <div v-if="modelValue?.id" class="rounded-app-control border border-info-200 bg-info-50 px-4 py-3 text-sm font-bold text-info-700">
      Вы изменяете существующую смену за {{ formatDate(form.date) }}.
    </div>

    <div v-if="!modelValue?.id" class="flex flex-wrap gap-2">
      <BaseButton v-if="state.shifts.length" variant="secondary" size="sm" @click="useLastShift">Последняя смена</BaseButton>
      <BaseButton v-if="state.settings.quickShift?.enabled" variant="secondary" size="sm" @click="useQuickTemplate">Типовая смена</BaseButton>
      <BaseButton variant="ghost" size="sm" @click="clearForm">Очистить</BaseButton>
    </div>

    <fieldset class="form-section">
      <legend>1. Дата и время</legend>
      <BaseInput v-model="form.date" label="Дата смены" type="date" required />

      <div class="mt-4 grid gap-4 sm:grid-cols-2">
        <TimeInput v-model="form.startTime" label="Начало" :error="submitted.value ? errors.startTime : ''" show-now />
        <TimeInput v-model="form.endTime" label="Окончание" :error="submitted.value ? errors.endTime : ''" />
      </div>

      <div class="mt-3 rounded-app-control bg-white p-3 ring-1 ring-app-border">
        <div class="flex items-center justify-between gap-3">
          <span class="text-sm font-bold text-app-muted">Продолжительность</span>
          <div class="text-right">
            <strong class="text-base text-app-text">{{ durationLabel }}</strong>
            <span v-if="crossesMidnight" class="ml-2 rounded-full bg-info-50 px-2 py-1 text-[10px] font-black text-info-700">до следующего дня</span>
          </div>
        </div>

        <div class="mt-3 flex gap-2 overflow-x-auto pb-1" aria-label="Быстрый выбор продолжительности">
          <button
            v-for="hours in quickDurations"
            :key="hours"
            type="button"
            class="min-h-11 shrink-0 rounded-app-control border px-4 text-sm font-black"
            :class="Math.abs(form.hours - hours) < 0.01 ? 'border-slate-950 bg-slate-950 text-white' : 'border-app-border bg-white text-app-text'"
            @click="setDuration(hours)"
          >
            {{ hours }} ч
          </button>
        </div>

        <div class="mt-3 sm:hidden">
          <p class="mb-2 text-xs font-black uppercase tracking-wider text-app-muted">Быстрое начало</p>
          <div class="grid grid-cols-4 gap-2">
            <button
              v-for="time in quickStarts"
              :key="time"
              type="button"
              class="min-h-11 rounded-lg border text-xs font-black"
              :class="form.startTime === time ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-app-border bg-white text-app-muted'"
              @click="setStartTime(time)"
            >
              {{ time }}
            </button>
          </div>
        </div>

        <p class="mt-3 text-xs font-semibold leading-5 text-app-muted">
          На телефоне используется крупный системный выбор времени, а кнопки позволяют сдвигать значение по 15 минут. Окончание в 00:00 считается полуночью следующего дня.
        </p>
      </div>
    </fieldset>

    <fieldset class="form-section">
      <legend>2. Результаты смены</legend>
      <BaseInput v-model="form.orders" label="Количество заказов" type="number" min="0" step="1" inputmode="numeric" :error="submitted.value ? errors.orders : ''" />
    </fieldset>

    <fieldset class="form-section">
      <legend>3. Условия</legend>
      <p class="mb-3 text-sm font-semibold text-app-muted">Отметьте условия, которые влияют на надбавки.</p>
      <div class="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <button
          v-for="condition in conditions"
          :key="condition.code"
          type="button"
          class="min-h-12 rounded-app-control border px-3 py-2 text-left text-sm font-black transition"
          :class="form.conditionCodes.includes(condition.code) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-app-border bg-white text-app-text hover:bg-slate-50'"
          :aria-pressed="form.conditionCodes.includes(condition.code)"
          @click="toggleCondition(condition.code)"
        >
          <span class="mr-1">{{ condition.emoji }}</span>{{ condition.title }}
        </button>
      </div>
    </fieldset>

    <fieldset class="form-section">
      <legend>4. Корректировки и комментарий</legend>
      <BaseInput v-model="form.manualAdjustment" label="Ручная корректировка, ₽" type="number" step="0.01" hint="Положительное значение добавляет сумму, отрицательное уменьшает." />
      <BaseTextarea v-model="form.comment" class="mt-4" label="Комментарий" placeholder="Необязательная заметка о смене" />
    </fieldset>

    <fieldset class="form-section">
      <legend>5. Предварительный расчёт</legend>
      <div class="divide-y divide-app-border overflow-hidden rounded-app-control border border-app-border bg-white text-sm">
        <div class="calculation-row"><span>Доход по часам</span><b>{{ formatCurrency(preview.hourlyIncome, state.settings.currency) }}</b></div>
        <div class="calculation-row"><span>Доход по заказам</span><b>{{ formatCurrency(preview.orderIncome, state.settings.currency) }}</b></div>
        <div class="calculation-row"><span>Надбавки</span><b class="text-success-700">+{{ formatCurrency(preview.bonusesTotal, state.settings.currency) }}</b></div>
        <div v-if="preview.manualAdjustment" class="calculation-row"><span>Ручная корректировка</span><b>{{ formatCurrency(preview.manualAdjustment, state.settings.currency) }}</b></div>
        <div class="calculation-row"><span>Вычеты</span><b class="text-danger-700">−{{ formatCurrency(preview.deductionsTotal, state.settings.currency) }}</b></div>
        <div class="flex items-center justify-between bg-slate-950 px-4 py-4 text-white"><span class="font-black">Итого к выплате</span><b class="text-xl">{{ formatCurrency(preview.net, state.settings.currency) }}</b></div>
      </div>
      <p v-if="submitted.value && (errors.startTime || errors.endTime || errors.orders || !form.hours)" class="mt-2 text-sm font-bold text-danger-700">
        Исправьте ошибки в форме перед сохранением.
      </p>
    </fieldset>

    <div class="sticky bottom-0 -mx-5 flex gap-3 border-t border-app-border bg-white/95 px-5 pb-[max(env(safe-area-inset-bottom,0px),.5rem)] pt-4 backdrop-blur sm:-mx-7 sm:px-7">
      <BaseButton variant="secondary" block :disabled="loading" @click="emit('cancel')">Отмена</BaseButton>
      <BaseButton type="submit" variant="brand" block :loading="loading">{{ submitLabel }}</BaseButton>
    </div>
  </form>
</template>
